import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CreateShippingCompanyRequestInDTO,
  EditShippingCompanyRequestInDTO,
  ListShippingCompaniesItemResponseOutDTO,
} from '@api/ringil-api.v2';
import useForm from '@hooks/useForm/useForm';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';

interface ComponentProps {
  data?: ListShippingCompaniesItemResponseOutDTO;
  getAllPositions: (callback: (positions: number[]) => void) => void;
  onChanged: () => void;
}

type FormValues = CreateShippingCompanyRequestInDTO | EditShippingCompanyRequestInDTO;

export const useAddEditShipCompanySidebar = () => {
  const [visible, setVisible] = useState(false);

  const AddEditShipCompanySidebar: React.FC<ComponentProps> = ({ data, getAllPositions, onChanged }) => {
    const { tr } = useTranslationLgs();
    const { adminCreateShippingCompany, adminEditShippingCompany } = useContext(ApiContext);
    const [positionError, setPositionError] = useState<string | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [nextAvailablePosition, setNextAvailablePosition] = useState<number>(0);

    // Calculate next available position when sidebar opens for new items
    useEffect(() => {
      if (visible && !data) {
        // Only calculate for new items (not editing)
        getAllPositions((positions) => {
          if (positions.length === 0) {
            setNextAvailablePosition(0);
          } else {
            const maxPosition = Math.max(...positions);
            const nextPos = maxPosition + 1;
            setNextAvailablePosition(nextPos);
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, data]);

    const validationSchema = yup.object().shape({
      name: yup
        .string()
        .required(tr('AddEditShipCompanySidebar.nameRequired', 'Name is required'))
        .max(255, tr('AddEditShipCompanySidebar.nameMax', 'Name must not exceed 255 characters')),
      position: yup
        .number()
        .required(tr('AddEditShipCompanySidebar.positionRequired', 'Position is required'))
        .integer(tr('AddEditShipCompanySidebar.positionInteger', 'Position must be a whole number'))
        .min(0, tr('AddEditShipCompanySidebar.positionMin', 'Position must be a positive number')),
    });

    const form = useForm<FormValues>(
      validationSchema,
      data ?? {
        name: '',
        position: nextAvailablePosition,
      },
      values => {
        // This is called after basic validation passes
        // Now check for position uniqueness
        validatePositionUniqueness(values);
      },
      false,
      false,
    );

    // Update form position when nextAvailablePosition changes (for create mode)
    useEffect(() => {
      if (!data && nextAvailablePosition > 0) {
        form.setFieldValue(form.names.position, nextAvailablePosition);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextAvailablePosition, data]);

    const validatePositionUniqueness = (values: FormValues) => {
      setIsValidating(true);
      setPositionError(null);

      getAllPositions((positions) => {
        // When editing, exclude the current item's position
        const positionsToCheck = data
          ? positions.filter((p) => p !== data.position)
          : positions;

        // Check if position is already taken
        const isDuplicate = positionsToCheck.includes(values.position);

        if (isDuplicate) {
          setPositionError(tr('AddEditShipCompanySidebar.positionUnique', 'This position is already in use'));
          setIsValidating(false);
        } else {
          setPositionError(null);
          save(values);
        }
      });
    };

    const save = (values: FormValues) => {
      if (data) {
        // Edit mode
        adminEditShippingCompany(
          data.id,
          values as EditShippingCompanyRequestInDTO,
          () => {
            setIsValidating(false);
            setVisible(false);
            onChanged();
          }
        );
      } else {
        // Create mode
        adminCreateShippingCompany(
          values as CreateShippingCompanyRequestInDTO,
          () => {
            setIsValidating(false);
            setVisible(false);
            onChanged();
          },
        );
      }
    };

    return (
      <RingilSidebar
        visible={visible}
        onBack={() => {
          setVisible(false);
          setPositionError(null);
          setIsValidating(false);
        }}
        heading={
          tr(
            data ? 'AddEditShipCompanySidebar.editTitle' : 'AddEditShipCompanySidebar.createTitle',
            data ? 'Edit Shipping Company' : 'Create Shipping Company',
          )
        }
        onContinue={() => {
          if (!isValidating) {
            form.validateAndSend();
          }
        }}
      >
        <div className={'flex flex-col gap-4'}>
          <Field
            label={tr('AddEditShipCompanySidebar.name', 'Name')}
            errorMessage={form.errors.name}
            required
          >
            <RingilInputText
              value={form.values.name}
              onChange={v => form.setFieldValue(form.names.name, v)}
              fullWidth
            />
          </Field>
          <Field
            label={tr('AddEditShipCompanySidebar.position', 'Position')}
            errorMessage={positionError || form.errors.position}
            required
          >
            <RingilInputText
              value={form.values.position?.toString() ?? ''}
              onChange={v => {
                setPositionError(null); // Clear error when user changes position
                form.setFieldValue(form.names.position, v ? parseInt(v, 10) : 0);
              }}
              fullWidth
              type="number"
            />
          </Field>
        </div>
      </RingilSidebar>
    );
  };

  return { setVisible, AddEditShipCompanySidebar: AddEditShipCompanySidebar };
};
